import { useCallback, useEffect, useRef, useState } from 'react';
import {
  detectCapabilities,
  GuestApi,
  GuestClient,
  HttpError,
  JoinController,
  openUplinkCapture,
  ReturnFeedPlayer,
  riftWtConnector,
  tokenExpiryMs,
  Uplink,
  type Capabilities,
  type GuestClientStats,
  type GuestConnectionState,
  type JoinState,
} from '@jeket-cloud/rift-guest-client';

// Browser listener default from participant-loop-contracts §2.1; a build can
// point elsewhere with VITE_RIFT_GATEWAY_URL (local gateways, staging).
const DEFAULT_GATEWAY_URL = import.meta.env.VITE_RIFT_GATEWAY_URL || 'https://rift.jeket.com/rift';

const connector = riftWtConnector(() => import('@jeket-cloud/rift-guest-client/rift-wt'));

function guestUserId(): string {
  const key = 'jeket_guest_user_id';
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(key, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export type AudioState = 'aac' | 'unsupported' | 'muted' | 'no-mic';

export interface GuestSession {
  caps: Capabilities | null;
  join: JoinState;
  devices: { cameras: MediaDeviceInfo[]; mics: MediaDeviceInfo[] };
  cameraId: string;
  micId: string;
  preview: MediaStream | null;
  mediaError: string | null;
  connection: GuestConnectionState;
  stats: GuestClientStats | null;
  micMuted: boolean;
  cameraOff: boolean;
  audioState: AudioState;
  setCameraId: (id: string) => void;
  setMicId: (id: string) => void;
  acknowledgeNotice: () => void;
  requestJoin: (displayName: string) => void;
  toggleMic: () => void;
  toggleCamera: () => void;
  leave: () => void;
  returnCanvas: React.RefObject<HTMLCanvasElement>;
}

export function useGuestSession(slug: string): GuestSession {
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [join, setJoin] = useState<JoinState>({
    phase: 'loading',
    event: null,
    participantId: null,
    slot: null,
    noticeAcknowledged: false,
    error: null,
  });
  const [devices, setDevices] = useState<{ cameras: MediaDeviceInfo[]; mics: MediaDeviceInfo[] }>({ cameras: [], mics: [] });
  const [cameraId, setCameraId] = useState('');
  const [micId, setMicId] = useState('');
  const [preview, setPreview] = useState<MediaStream | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [connection, setConnection] = useState<GuestConnectionState>('idle');
  const [stats, setStats] = useState<GuestClientStats | null>(null);
  const [micMuted, setMicMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);

  const controllerRef = useRef<JoinController | null>(null);
  const clientRef = useRef<GuestClient | null>(null);
  const uplinkRef = useRef<Uplink | null>(null);
  const playerRef = useRef<ReturnFeedPlayer | null>(null);
  const returnCanvas = useRef<HTMLCanvasElement>(null);
  const micMutedRef = useRef(false);
  const cameraOffRef = useRef(false);

  const hasMic = !!preview?.getAudioTracks().length;
  const audioState: AudioState = !hasMic ? 'no-mic' : caps && !caps.aacEncode ? 'unsupported' : micMuted ? 'muted' : 'aac';
  const audioStateRef = useRef(audioState);
  audioStateRef.current = audioState;

  // Capabilities + event lookup. The JoinController keeps the participant id
  // and the guest token the join answers, in memory only, so a reload is a
  // fresh join. Unmounting is not a Leave (contracts v2 §6): it stops the
  // controller and, below, the session; the gateway then reports the guest
  // disconnected and event-manager removes them after the presence timeout.
  // Only the Leave button sends the DELETE.
  useEffect(() => {
    let alive = true;
    void detectCapabilities().then((c) => alive && setCaps(c));
    const controller = new JoinController({ api: new GuestApi('/api'), slug, onChange: (s) => alive && setJoin(s) });
    controllerRef.current = controller;
    void controller.load();
    return () => {
      alive = false;
      controller.dispose();
    };
  }, [slug]);

  // Camera/mic preview, re-acquired when the guest picks another device
  // (before joining; the pipeline reads fixed tracks once live).
  useEffect(() => {
    if (!caps?.supported || uplinkRef.current) return;
    let stream: MediaStream | null = null;
    let alive = true;
    // Ask for the uplink raster up front, at 24 fps or better where the camera
    // has such a mode; whatever it still delivers is letterboxed/decimated to
    // exactly 1280x720@30 in the Uplink.
    openUplinkCapture((c) => navigator.mediaDevices.getUserMedia(c), cameraId, {
      deviceId: micId ? { exact: micId } : undefined,
      channelCount: { ideal: 2 },
      echoCancellation: true,
      noiseSuppression: true,
    })
      .then(async (s) => {
        if (!alive) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        setPreview(s);
        setMediaError(null);
        const all = await navigator.mediaDevices.enumerateDevices();
        setDevices({
          cameras: all.filter((d) => d.kind === 'videoinput'),
          mics: all.filter((d) => d.kind === 'audioinput'),
        });
      })
      .catch((err: Error) => setMediaError(err.name === 'NotAllowedError' ? 'Camera and microphone access was blocked.' : err.message));
    return () => {
      alive = false;
      if (!uplinkRef.current) stream?.getTracks().forEach((t) => t.stop());
    };
  }, [caps?.supported, cameraId, micId]);

  const stopMedia = useCallback(async () => {
    const client = clientRef.current;
    clientRef.current = null;
    await client?.stop();
    await uplinkRef.current?.stop();
    uplinkRef.current = null;
    playerRef.current?.close();
    playerRef.current = null;
  }, []);

  // Connect once the controller says the guest is admitted, and stay connected
  // until Leave: on a slot or not, the guest publishes and watches the return
  // feed, so a take is a route change at the gateway (contracts v2 §6).
  useEffect(() => {
    const controller = controllerRef.current;
    if (join.phase !== 'admitted' || !controller || !preview || !caps || clientRef.current) return;

    const audioEnabled = caps.aacEncode && preview.getAudioTracks().length > 0;
    if (!caps.aacEncode && join.event && join.participantId) {
      // No AAC encoder and no other codec JBS can demux: join video-only,
      // and tell the control plane so the mix does not wait on a dead mic.
      console.warn('[rift-guest] AAC encode unsupported in this browser; joining without audio');
      void controller.setMedia({ micEnabled: false }).catch(() => undefined);
    }
    const uplink = new Uplink(preview, audioEnabled);
    uplinkRef.current = uplink;
    const canvas = returnCanvas.current;
    const player = canvas ? new ReturnFeedPlayer(canvas) : null;
    player?.enableAudio();
    playerRef.current = player;

    let admittedToken = controller.token;
    const client = new GuestClient({
      url: DEFAULT_GATEWAY_URL,
      connector,
      getToken: async () => {
        // First connect reuses the token fetched at admission; every
        // reconnect/refresh asks event-manager for a fresh one.
        const t = admittedToken ?? (await controller.fetchToken());
        admittedToken = null;
        return { token: t.token, expiresAtMs: tokenExpiryMs(t) };
      },
      uplink,
      returnFeed: player ?? undefined,
      audioState: () => audioStateRef.current,
      onState: setConnection,
      onStats: setStats,
      // The gateway asks for a keyframe when it routes the guest onto a slot:
      // re-read the row now, so "You're live" does not wait for the next poll.
      onKeyframeRequest: () => controller.pollNow(),
      // When the gateway removes a guest it closes their session (4003) and
      // refuses their Hello (4001), and the client cannot see either code. So
      // after any close it reads the guest's row once: out ends the session,
      // admitted redials, and no answer redials (a lost network is not a Leave).
      confirmAdmitted: () => controller.confirmAdmitted(),
      onRefused: () => controller.markRefused(),
      shouldStop: (err) => {
        if (!JoinController.isOffAir(err)) return false;
        controller.markEnded(
          err instanceof HttpError && err.status === 409
            ? `This event isn't set up for guests yet (${err.message}).`
            : 'You are no longer in this event.',
        );
        return true;
      },
    });
    clientRef.current = client;
    void uplink.start().then(() => client.start());
    void controller.watchAdmitted();
  }, [join.phase, join.event, join.participantId, preview, caps]);

  // Out of the event (Leave, removed, rejected): tear the pipeline down and release the camera.
  useEffect(() => {
    if (join.phase !== 'ended') return;
    void stopMedia().then(() => {
      preview?.getTracks().forEach((t) => t.stop());
      setPreview(null);
    });
  }, [join.phase, preview, stopMedia]);

  useEffect(() => () => void stopMedia(), [stopMedia]);

  const requestJoin = useCallback(
    (displayName: string) => {
      // The click is the user gesture the return-feed AudioContext needs;
      // the player itself is created once on air.
      void controllerRef.current?.join(guestUserId(), displayName);
    },
    [],
  );

  const toggleMic = useCallback(() => {
    const next = !micMutedRef.current;
    micMutedRef.current = next;
    // A disabled track keeps producing (silent) AudioData, so the encoder,
    // the TS timeline and the ISO stay continuous.
    preview?.getAudioTracks().forEach((t) => (t.enabled = !next));
    setMicMuted(next);
    // The controller holds the guest's token (X-Participant-Token); a no-op before joining.
    void controllerRef.current?.setMedia({ micEnabled: !next }).catch(() => undefined);
  }, [preview]);

  const toggleCamera = useCallback(() => {
    const next = !cameraOffRef.current;
    cameraOffRef.current = next;
    // Disabled video tracks deliver black frames: keyframes and PCR keep
    // flowing, so the gateway's ISO segmenter and JBS's decoder do too.
    preview?.getVideoTracks().forEach((t) => (t.enabled = !next));
    setCameraOff(next);
    void controllerRef.current?.setMedia({ cameraEnabled: !next }).catch(() => undefined);
  }, [preview]);

  const leave = useCallback(() => {
    void stopMedia().then(() => controllerRef.current?.leave());
  }, [stopMedia]);

  return {
    caps,
    join,
    devices,
    cameraId,
    micId,
    preview,
    mediaError,
    connection,
    stats,
    micMuted,
    cameraOff,
    audioState,
    setCameraId,
    setMicId,
    acknowledgeNotice: () => controllerRef.current?.acknowledgeNotice(),
    requestJoin,
    toggleMic,
    toggleCamera,
    leave,
    returnCanvas,
  };
}
